import {
  Component,
  OnInit,
  viewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { take } from 'rxjs/operators';
import {
  LazyLoadFilters,
  PageRequest,
  SearchContainerComponent,
} from 'src/app/core/containers/search-container/search-container.component';
import { TabService } from 'src/app/core/services/tab.service';
import { Location } from 'src/app/model/locations/location';
import { LocationTableService } from 'src/app/services/table/location-table.service';
import { AdminApiService } from 'src/app/services/api/admin-api.service';
import { LocationApiService } from '../../../services/api/location-api.service';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { LOCATION_TABLE_DEFINITION } from '../../definitions/location-table-definition';
import { BreadCrumbBuilderService } from 'src/app/services/builder/breadcrumb-builder.service';
import { BusinessUnit } from 'src/app/model/admin/business-unit';
import { isNullOrUndefined } from 'src/app/utils/utils';
import { BusinessUnitFilterComponent } from 'src/app/core/components/business-unit-filter/business-unit-filter.component';
import { CLAIMS } from 'src/app/constants/auth-constants';
import {
  AutoCompleteCompleteEvent,
  AutoCompleteSelectEvent,
  AutoComplete,
} from '@openng/optimus-ui/autocomplete';
import { GridLazyLoadEvent } from 'src/app/core/components/base-grid.component';
import { FilterMetadata, PrimeTemplate } from '@openng/optimus-ui/api';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { GeneralGridComponent } from '../../../core/components/general-grid.component';
import { Panel } from '@openng/optimus-ui/panel';
import { LocationMobileFilterComponent } from '../../components/location-mobile-filter/location-mobile-filter.component';
import { Dialog } from '@openng/optimus-ui/dialog';
import { PickList } from '@openng/optimus-ui/picklist';
import { InputText } from '@openng/optimus-ui/inputtext';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { dynamicSort } from 'src/app/core/functions/common-functions';

@Component({
  selector: 'app-location-container',
  templateUrl: './location-container.component.html',
  styleUrls: ['./location-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BlockUI,
    ProgressSpinnerComponent,
    BusinessUnitFilterComponent,
    ButtonDirective,
    GeneralGridComponent,
    Panel,
    LocationMobileFilterComponent,
    Dialog,
    FormsModule,
    ReactiveFormsModule,
    PickList,
    PrimeTemplate,
    AutoComplete,
    InputText,
    TranslatePipe,
  ],
})
export class LocationContainerComponent
  extends SearchContainerComponent<Location>
  implements OnInit
{
  protected _translateService = inject(TranslateService);
  protected _breadCrumbBuilderService = inject(BreadCrumbBuilderService);
  protected _fb = inject(UntypedFormBuilder);
  protected _locationApi = inject(LocationApiService);
  protected _locationTableService = inject(LocationTableService);
  protected _authApi = inject(AuthApiService);
  protected _adminApi = inject(AdminApiService);
  protected _tabService = inject(TabService);
  protected _logAndMessage = inject(LogAndMessageService);

  readonly businessUnitFilter =
    viewChild<BusinessUnitFilterComponent>('buFilter');
  allLocations: any[] = [];
  locationCreateForm?: UntypedFormGroup;
  displayDialog = false;
  filteredStates: any[] = [];
  filteredCounties: any[] = [];
  filteredTownships: any[] = [];
  itemsPerPage = 1948;
  finishedFetching = false;
  subscription: any;
  tick: any;
  shouldLoadMore = 10;
  first = false;
  count = 0;
  availableStates: any[] = [];
  availableCounties: any[] = [];
  availableTownships: any[] = [];
  canCreate = false;
  canEdit = false;
  saving = false;
  isEditting = false;
  selectedLocation: Location | null = null;
  mobileFilterCollapsed = true;

  availableBusinessUnits: BusinessUnit[] = [];
  sourcePicklist: BusinessUnit[] = [];
  targetPicklist: BusinessUnit[] = [];

  constructor() {
    super();
  }

  ngOnInit() {
    this.setTableService(this._locationTableService);
    this.locationCreateForm = this._fb.group({
      state: [null, Validators.required],
      county: [null, Validators.required],
      township: [null, Validators.required],
      id: [null],
      businessUnits: [{ value: null, disabled: false }],
    });

    if (!this.isEditting) {
      this.locationCreateForm.disable();
    }

    this.initialize();
  }

  initialize() {
    this._translateService
      .get('LOCATION.SCREEN.LOCATION')
      .subscribe((label) => {
        if (this._deviceService.isMobile()) {
          this._breadCrumbBuilderService.resetAndAddBreadCrumb({
            label: label,
            routerLink: null,
          });
        } else {
          this._tabService.updateActiveTabLabel(label);
        }
        this.screenName = label;
      });

    this._adminApi.getAvailableBusinessUnits().subscribe((businessUnits) => {
      this.availableBusinessUnits = businessUnits;
    });

    this.canCreate = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.LOCATIONS.LOCATIONS.CREATE_LOCATIONS,
    ]);
    this.canEdit = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.LOCATIONS.LOCATIONS.EDIT_LOCATIONS,
    ]);

    this.columns = LOCATION_TABLE_DEFINITION;
    this.applyPreviousData();
    this.applyPreviousFilters();

    this._locationApi.getStates().subscribe((states) => {
      this.availableStates = [...states].sort();
    });

    this._locationApi.getCounties().subscribe((counties) => {
      this.availableCounties = [...counties].sort(dynamicSort('county', 1));
    });

    this._locationApi.getTownships().subscribe((townships) => {
      this.availableTownships = [...townships].sort(dynamicSort('township', 1));
    });
  }

  /**
   * Method called when the user hits the clear button.
   */
  clearTable() {
    this.clearScreen();
    this.allLocations = [];
  }

  changeViewEvent(event: { data: Location }) {
    this.selectedLocation = event.data;
    this.locationCreateForm?.patchValue({
      state: event.data.state,
      county: { county: event.data.county },
      township: event.data.township,
      id: event.data.id,
      updater: event.data.id,
      businessUnits: event.data.businessUnits,
    });
    const businessUnits =
      this.locationCreateForm?.controls['businessUnits'].value;
    let currentBusinessUnits: string[] = [];
    businessUnits.forEach((x: BusinessUnit) => {
      currentBusinessUnits.push(x.name);
    });
    this.sourcePicklist = [];
    this.targetPicklist = [];
    this.availableBusinessUnits.forEach((x) => {
      if (currentBusinessUnits.includes(x.name)) {
        this.targetPicklist.push(x);
      } else {
        this.sourcePicklist.push(x);
      }
    });

    this.displayDialog = true;
    this.locationCreateForm?.disable();
  }

  newLocation() {
    this.locationCreateForm?.reset();
    this.sourcePicklist = [];
    this.targetPicklist = [];
    this.availableBusinessUnits.forEach((x) => {
      this.sourcePicklist.push(x);
    });
    this.isEditting = true;
    this.displayDialog = true;
    if (this.canCreate) {
      this.locationCreateForm?.enable();
    }
  }

  deleteLocation() {
    if (this.canEdit) {
      const id = this.locationCreateForm?.controls['id'].value;
      if (!id) {
        return;
      }

      this._locationApi.deleteLocation(id).subscribe({
        next: (result) => {
          this.clearForm();
          this.displayDialog = false;
          this.lazyLoad({
            lazy: this._locationTableService.getLastLazyLoad()!,
          });
        },
        error: (error) => {
          console.error('Error deleting location', error);
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
            bodyKey: 'LOCATION.MESSAGES.ERROR.DELETE_LOCATION',
          });
        },
      });
    }
  }

  createLocation(location: any) {
    if (!this.canCreate) {
      return;
    }
    location.updater = null;
    location.updatedAt = null;
    location.createdAt = null;
    const assignedUnits: BusinessUnit[] = !isNullOrUndefined(
      this.targetPicklist
    )
      ? this.targetPicklist
      : [];
    const locationInput = Object.assign(
      {},
      {
        state: location.state,
        county: location.county,
        township: location.township,
        businessUnits: assignedUnits.map((m) => ({
          id: m.id,
          name: m.name,
        })),
      }
    );

    this.locationCreateForm?.disable();

    this._locationApi.createLocation(locationInput).subscribe({
      next: (result) => {
        this.clearForm();
        this.displayDialog = false;
        this.lazyLoad({
          lazy: this._locationTableService.getLastLazyLoad()!,
        });
        this.isEditting = false;
        this.locationCreateForm?.disable();
      },
      error: (error) => {
        console.error('Error creating location', error);
        this._logAndMessage.translateToErrorAlert({
          headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          bodyKey: 'LOCATION.MESSAGES.ERROR.CREATE_LOCATION',
        });
        this.isEditting = true;
        this.locationCreateForm?.enable();
      },
    });
  }

  updateLocation(location: any) {
    if (!this.canEdit) {
      return;
    }
    const assignedUnits: BusinessUnit[] = !isNullOrUndefined(
      this.targetPicklist
    )
      ? this.targetPicklist
      : [];
    const locationInput = Object.assign(
      {},
      {
        state: location.state,
        county: location.county,
        township: location.township,
        businessUnits: assignedUnits.map((m) => ({
          id: m.id,
          name: m.name,
        })),
      }
    );

    this.locationCreateForm?.disable();

    this._locationApi
      .updateLocation(location.id, locationInput)
      .pipe(take(1))
      .subscribe({
        next: (result) => {
          this.clearForm();
          this.displayDialog = false;
          this.lazyLoad({
            lazy: this._locationTableService.getLastLazyLoad()!,
          });
          this.isEditting = false;
          this.locationCreateForm?.disable();
        },
        error: (error) => {
          console.error('Error updating location', error);
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
            bodyKey: 'LOCATION.MESSAGES.ERROR.UPDATE_LOCATION',
          });
          this.isEditting = true;
          this.locationCreateForm?.enable();
        },
      });
  }

  saveLocation() {
    if (!this.canEdit && !this.canCreate) {
      return;
    }

    if (this.locationCreateForm?.valid) {
      const location = Object.assign({}, this.locationCreateForm.getRawValue());
      // need to support objects and user input.
      if (location.county.hasOwnProperty('county')) {
        location.county = location.county.county;
      }
      if (location.township.hasOwnProperty('township')) {
        location.township = location.township.township;
      }
      if (!location.id) {
        this.createLocation(location);
      } else {
        this.updateLocation(location);
      }
    }
  }

  clearForm() {
    this.isEditting = false;
    this.locationCreateForm?.reset();
  }

  stateSearch($event: AutoCompleteCompleteEvent) {
    if ($event.query === '') {
      this.filteredStates = [...this.availableStates];
    } else {
      this.filteredStates = [
        ...this.availableStates.filter((c) =>
          c.toLowerCase().startsWith($event.query.toLowerCase())
        ),
      ];
    }
  }

  countySearch($event: AutoCompleteCompleteEvent) {
    const state = this.locationCreateForm?.controls['state'].value;
    if ($event.query === '') {
      if (!state) {
        this.filteredCounties = [...this.availableCounties];
      } else {
        this.filteredCounties = [
          ...this.availableCounties.filter((c) => c.state === state),
        ];
      }
    } else {
      if (!state) {
        this.filteredCounties = [
          ...this.availableCounties.filter((c) =>
            c.county.toLowerCase().includes($event.query.toLowerCase())
          ),
        ];
      } else {
        this.filteredCounties = [
          ...this.availableCounties.filter(
            (c) =>
              c.county.toLowerCase().includes($event.query.toLowerCase()) &&
              c.state === state
          ),
        ];
      }
    }
  }

  selectCounty($event: AutoCompleteSelectEvent): void {
    this.locationCreateForm?.patchValue({
      county: $event.value.county,
      state: $event.value.state,
    });
  }

  townshipSearch($event: AutoCompleteCompleteEvent) {
    const state = this.locationCreateForm?.controls['state'].value;
    const county = this.locationCreateForm?.controls['county'].value;
    if (state) {
      if (county) {
        if ($event.query === '') {
          this.filteredTownships = [
            ...this.availableTownships.filter(
              (c) => c.state === state && c.county === county
            ),
          ];
        } else {
          this.filteredTownships = [
            ...this.availableTownships.filter(
              (c) =>
                c.state === state &&
                c.county === county &&
                c.township.toLowerCase().includes($event.query.toLowerCase())
            ),
          ];
        }
      } else {
        if ($event.query === '') {
          this.filteredTownships = [
            ...this.availableTownships.filter((c) => c.state === state),
          ];
        } else {
          this.filteredTownships = [
            ...this.availableTownships.filter(
              (c) =>
                c.state === state &&
                c.township.toLowerCase().includes($event.query.toLowerCase())
            ),
          ];
        }
      }
    } else {
      if ($event.query === '') {
        this.filteredTownships = [...this.availableTownships];
      } else {
        this.filteredTownships = [
          ...this.availableTownships.filter((c) =>
            c.township.toLowerCase().includes($event.query.toLowerCase())
          ),
        ];
      }
    }
  }

  selectTownship($event: Location) {
    this.locationCreateForm?.patchValue({
      locationId: $event.id,
      state: $event.state,
      county: { county: $event.county, state: $event.state },
    });
  }

  queryNetwork(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    query: { [key: string]: any }
  ) {
    req.page += 1;
    const sort = this.buildNetworkSortQuery($event, 'state');
    this._locationApi
      .queryForLocations(req.pageSize, req.page, query, sort)
      .subscribe({
        next: (locationConnection) => {
          const locs = locationConnection.items;
          this.totalRecords = locationConnection.totalRecords;
          this.elements = [...locs];
          this.loading = false;
        },
        error: (error) => {
          console.error(error);
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
            bodyKey: 'LOCATION.MESSAGES.ERROR.QUERY_LOCATIONS',
          });
          this.loading = false;
        },
      });
  }

  override handleLazyLoad(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    filters: LazyLoadFilters
  ) {
    let selectedBusinessUnits = [];
    const businessUnitFilter = this.businessUnitFilter();
    if (businessUnitFilter?.selectedBusinessUnits) {
      // Handle further refreshes & changes to business unit filter component
      selectedBusinessUnits = businessUnitFilter.selectedBusinessUnits;
    } else {
      // Handle initial page load (before the business unit filter subcomponent exists)
      selectedBusinessUnits = this._authApi
        .getUserBusinessUnits()
        .map((bu) => bu.id);
    }
    filters['businessUnits'] = selectedBusinessUnits as FilterMetadata;

    const query: any = {
      state: null,
      county: null,
      township: null,
      businessUnits: null,
      businessUnitString: null,
    };

    if (filters.state) {
      query.state = (filters.state as FilterMetadata).value;
    }
    if (filters.county) {
      query.county = (filters.county as FilterMetadata).value;
    }
    if (filters.township) {
      query.township = (filters.township as FilterMetadata).value;
    }
    if (filters.businessUnits) {
      query.businessUnits = filters.businessUnits;
    }
    if (filters.businessUnitString) {
      query.businessUnitString = (
        filters.businessUnitString as FilterMetadata
      )?.value;
    }

    this.queryNetwork(req, $event, query);
  }

  handleBusinessUnitChange() {
    this.lazyLoad({ lazy: this.tableService.getLastLazyLoad()! });
  }

  editLocation() {
    this.isEditting = true;
    this.locationCreateForm?.enable();
  }

  mobileFilter($event: any) {
    const newLazy: any = {
      filters: {
        ...this.grid()?.table()?.filters,
        state: $event.state
          ? {
              value: $event.state,
              matchMode: 'contains',
            }
          : null,
        county: $event.county
          ? {
              value: $event.county,
              matchMode: 'contains',
            }
          : null,
        township: $event.township
          ? {
              value: $event.township,
              matchMode: 'contains',
            }
          : null,
      },
    };
    this.mobileFilterCollapsed = true;
    this._locationTableService.setLastLazyLoad(newLazy);
    this.lazyLoad({ lazy: this._locationTableService.getLastLazyLoad()! });
  }

  refresh(): void {
    this.lazyLoad({ lazy: this._locationTableService.getLastLazyLoad()! });
  }
}
